import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCreateShow, useShows, type Show } from '../lib/shows';
import { Button } from '../components/ui/Button';
import { EmptyState, ErrorMessage, Loading, PageHeader } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { ShowFormDialog } from '../components/shows/ShowFormDialog';

export function PodcastsPage() {
  const shows = useShows();
  const create = useCreateShow();
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const newShow = (
    <Button
      onClick={() => {
        create.reset();
        setOpen(true);
      }}
    >
      New show
    </Button>
  );

  return (
    <div>
      <PageHeader title="Shows" description="Each show has its own episodes and RSS feed." actions={shows.data?.podcasts.length ? newShow : undefined} />

      {shows.isLoading && <Loading />}
      {shows.isError && <ErrorMessage error={shows.error} onRetry={() => shows.refetch()} />}
      {shows.data && shows.data.podcasts.length === 0 && (
        <EmptyState
          title="Create your first show"
          body="Add a show, upload an episode and publish it to Apple Podcasts, Spotify and every other app from one RSS feed."
          action={newShow}
        />
      )}
      {shows.data && shows.data.podcasts.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shows.data.podcasts.map((p) => (
            <li key={p.id}>
              <ShowCard show={p} />
            </li>
          ))}
        </ul>
      )}

      {open && (
        <ShowFormDialog
          open
          onClose={() => setOpen(false)}
          submitting={create.isPending}
          error={create.error}
          onSubmit={(values) =>
            create.mutate(
              {
                title: values.title,
                author: values.author || undefined,
                description: values.description || undefined,
                category: values.category || undefined,
                language: values.language || 'en',
                explicit: values.explicit,
                ...(values.ownerEmail ? { ownerEmail: values.ownerEmail } : {}),
              },
              {
                onSuccess: (show) => {
                  setOpen(false);
                  toast.show(`Created “${show.title}”`, 'success');
                  navigate(`/podcasts/${show.id}`);
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

function ShowCard({ show }: { show: Show }) {
  return (
    <Link
      to={`/podcasts/${show.id}`}
      className="flex gap-4 rounded-xl border border-border bg-bg p-4 transition-colors hover:border-accent"
    >
      <Artwork show={show} />
      <div className="min-w-0">
        <h2 className="truncate font-semibold text-text">{show.title}</h2>
        <p className="text-xs text-muted">
          {show.episodeCount ?? 0} {show.episodeCount === 1 ? 'episode' : 'episodes'}
        </p>
        {show.description && <p className="mt-1 line-clamp-2 text-sm text-muted">{show.description}</p>}
      </div>
    </Link>
  );
}

export function Artwork({ show, size = 'size-16' }: { show: Pick<Show, 'title' | 'artworkUrl'>; size?: string }) {
  if (show.artworkUrl) {
    return <img src={show.artworkUrl} alt="" className={`${size} shrink-0 rounded-lg object-cover`} />;
  }
  return (
    <div aria-hidden className={`${size} flex shrink-0 items-center justify-center rounded-lg bg-accent-soft text-xl font-bold text-accent`}>
      {show.title.charAt(0).toUpperCase()}
    </div>
  );
}
